import type { InterleavedWord } from 'js/src/agent/tools/transcript-tokens';
import { interleavedTokenToString } from 'js/src/agent/tools/transcript-tokens';

/**
 * The transcript context an asset's scene sits in: the scene group's interleaved tokens plus the
 * translation from video time to transcript time (which honours cuts in the narration), so an
 * asset's appearance point can be named as the word being spoken. Undefined for text-on-media
 * scenes, which have no transcript.
 */
export interface AssetTimingTranscriptContext {
    tokens: InterleavedWord[];
    videoTimeToTranscriptTime: (videoTimeMs: number) => number;
}

/** The subset of an asset the timing description reads. */
interface TimedAsset {
    /** When the asset actually lands, relative to its container — group stagger included. */
    appliedIntroAnimationOffset: number;
    /** The scene or subscene the asset sits on; its start is the origin the offset is applied from. */
    luminaryParent: { startTime: number } | null;
}

/**
 * How an asset's appearance point reads in the video state, or undefined when it simply appears
 * with its scene (the overwhelmingly common case — emitting "0ms" on every asset would be noise).
 *
 * Named as the transcript word being spoken when the asset lands, so the read vocabulary matches
 * the `start_words` the agent writes back through set_text / set_group_asset. Falls back to
 * milliseconds when there is no transcript (text-on-media) or the offset lands past the last word,
 * matching `start_time_ms`.
 */
export function describeAssetAppearsAt(
    asset: TimedAsset,
    transcript: AssetTimingTranscriptContext | undefined,
): string | undefined {
    const introOffset = asset.appliedIntroAnimationOffset;
    if (!Number.isFinite(introOffset) || introOffset <= 0) {
        return undefined;
    }

    if (transcript && asset.luminaryParent) {
        const introInTranscriptTime = transcript.videoTimeToTranscriptTime(
            asset.luminaryParent.startTime + introOffset,
        );
        const spokenToken = transcript.tokens.find(
            (token) =>
                token.endTime > introInTranscriptTime && token.startTime <= introInTranscriptTime,
        );
        if (spokenToken) {
            return `"${interleavedTokenToString(spokenToken)}"`;
        }
    }

    return `${Math.round(introOffset)}ms into the scene`;
}
